import { Button, Classes, Divider, Switch } from '@blueprintjs/core';
import { classNames } from '@axo/shared/util/dom';
import styles from './MarketingConsentCardSection.module.css';
import { AceCardSection } from '../AceCard';
import {
  lock_hooks,
  useApplicationDetail,
  useCustomer,
  useGetConsentConfig,
  useGetConsentItemsByCustomer,
  useGetConsentPartners,
  usePatchPartnerConsent,
} from '@axo/shared/data-access/hooks';
import { BlueprintIcons_16Id } from '@blueprintjs/icons/lib/esm/generated/16px/blueprint-icons-16';
import {
  DataAccessContext,
  useHasAnyOfRoles,
} from '@axo/shared/data-access/provider';
import { permissions } from '@axo/shared/data-access/types';
import { SmallTag } from '../SmallTag';
import { MarketCountryCode } from '@axo/shared/types';
import {
  CommunicationChannelType,
  ConsentData,
  ConsentStateType,
  Partner,
} from '@axo/shared/data-access/types/consent';
import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { BrandLogo } from '../BrandLogo';
import { ConsentSwitch } from './ConsentSwitch';
import { Brand } from '@axo/config/@types/Brand.types';
import React from 'react';
import { getBrandForGeneralConsent } from '@axo/config/util/get-brand-for-general-consent';
import { useAddMarketingConsent } from './hooks/useAddMarketingConsent';
import { TransactionalMuteSection } from './TransactionalMuteSection';
import {
  buildChannelPatch,
  buildRevokeForAllPatch,
  buildRevokeForPartnerPatch,
  hasAnyGivenMarketing,
  isMarketingConsentGiven,
  isMarketingConsentRevoked,
} from './utils/consentPatches';

export interface IApplicantCard {
  applicationID: string;
  customerID: string;
}

const MarketingConsentTag = ({
  isAllowed,
  text,
  icon,
}: {
  isAllowed: boolean;
  text: string;
  icon: BlueprintIcons_16Id;
}) => (
  <SmallTag intent={isAllowed ? 'success' : 'danger'} minimal round icon={icon}>
    {text}
  </SmallTag>
);

export function MarketingPartnerConsentCardSection({
  applicationID,
  customerID,
}: IApplicantCard) {
  const {
    state: { user },
  } = useContext(DataAccessContext);
  const [selectedPartner, setSelectedPartner] = useState<undefined | number>(
    undefined
  );
  const [selectedPartnerAndBrand, setSelectedPartnerAndBrand] = useState<
    Partner | undefined
  >(undefined);
  const [selectedBrandConsent, setSelectedBrandConsent] = useState<
    any | undefined
  >(undefined);

  const [showInactiveTags, setShowInactiveTags] = useState<boolean>(false);
  const partnerSelectId = useId();
  const customer = useCustomer(customerID);
  const application = useApplicationDetail(applicationID);
  const patchPartner = usePatchPartnerConsent();
  const { addMarketingConsent, isLoading: isAddingConsent } =
    useAddMarketingConsent();

  const consentPartners = useGetConsentPartners();

  const consentItems = useGetConsentItemsByCustomer(
    application.data?.MarketCountry as MarketCountryCode,
    customerID
  );

  // Get the marketing consent config by brand to retrieve the correct ConfigID
  const brandConfig = useGetConsentConfig({
    country: application.data?.MarketCountry as MarketCountryCode,
    brandID: application.data?.Meta?.Brand as Brand,
    configType: 'MARKETING',
    enabled: !!(
      application.data?.MarketCountry && application.data?.Meta?.Brand
    ),
  });

  // Fallback to GLOBAL brand if no config found for the specific brand
  const globalConfig = useGetConsentConfig({
    country: application.data?.MarketCountry as MarketCountryCode,
    brandID: 'GLOBAL' as Brand,
    configType: 'MARKETING',
    enabled: !!(application.data?.MarketCountry && !brandConfig.data?.[0]),
  });

  const consentConfig = brandConfig.data?.[0] || globalConfig.data?.[0];

  const isAddConsentButtonDisabled = useMemo(
    () =>
      !consentConfig?.ID || !application.data?.Meta?.Brand || isAddingConsent,

    [consentConfig?.ID, application.data?.Meta?.Brand, isAddingConsent]
  );

  const groupedConsentItems = consentItems.data?.Items.reduce(
    (map, consent) => {
      if (consent.Transactional) return map;
      if (!map.has(consent.PartnerID)) {
        map.set(consent.PartnerID, []);
      }
      map.get(consent.PartnerID)?.push(consent);
      return map;
    },
    new Map<number, any[]>()
  );

  // Partners with marketing items only: a partner with nothing but
  // transactional items has nothing to show or revoke.
  const partnersList = consentPartners.data?.filter((partner) =>
    groupedConsentItems?.has(partner.ID)
  );
  const partnerIds = partnersList?.map((partner) => partner.ID) ?? [];

  const generalConsentBrand = application.data?.Meta?.Brand
    ? getBrandForGeneralConsent(application.data.Meta.Brand)
    : undefined;
  const generalConsentPartner = partnersList?.find(
    (partner) => partner.BrandID === generalConsentBrand
  );
  const currentPartner = partnersList?.find(
    (partner) => partner.BrandID === application.data?.Meta?.Brand
  );

  const setSelectedPartnerOption = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const partnerId = parseInt(e.target.value);
    setSelectedPartner(partnerId);
    setSelectedBrandConsent(consentGroupedByChannel(partnerId));
    setSelectedPartnerAndBrand(
      partnersList?.find((item) => item.ID === partnerId)
    );
  };

  const onCollapsedAceCardSection = (collapsed: boolean) => {
    setShowInactiveTags(collapsed);
  };

  const onCheckedConsentSwitch = async (
    consentData: ConsentData,
    value: boolean
  ) => {
    await patchPartner.mutateAsync({
      country: application.data?.MarketCountry as MarketCountryCode,
      customerID,
      patchData: buildChannelPatch(consentData, value),
    });
  };

  const getIconByChannelType = (
    channel: CommunicationChannelType
  ): BlueprintIcons_16Id => {
    switch (channel) {
      case CommunicationChannelType.Email:
        return 'envelope';
      case CommunicationChannelType.Sms:
        return 'comment';
      case CommunicationChannelType.AiPhone:
        // Distinct from the plain 'phone' used for the human Call channel.
        return 'predictive-analysis';
      default:
        return 'phone';
    }
  };

  const consentGroupedByChannel = (
    partnerId: number
  ): Record<string, ConsentData[]> => {
    const partnerConsentItems = normalizeConsentItems(partnerId);
    return (
      partnerConsentItems?.reduce<Record<string, ConsentData[]>>(
        (acc, item) => {
          if (!acc[item.Channel]) {
            acc[item.Channel] = [];
          }
          acc[item.Channel].push(item);
          return acc;
        },
        {}
      ) ?? {}
    );
  };

  // Marketing channels only: transactional consent is no longer used in any
  // market (MV-3323), so ACE neither renders nor sends it.
  const normalizeConsentItems = (partnerId: number) => {
    const marketingConsentsOrder = [
      CommunicationChannelType.Email,
      CommunicationChannelType.Sms,
      CommunicationChannelType.Phone,
      CommunicationChannelType.AiPhone,
    ];
    const partnerConsentItems = groupedConsentItems?.get(partnerId);
    marketingConsentsOrder.forEach((consentType) => {
      if (!partnerConsentItems?.find((item) => item.Channel === consentType)) {
        partnerConsentItems?.unshift({
          Channel: consentType,
          Transactional: false,
        });
      }
    });
    return partnerConsentItems?.sort(
      (a, b) =>
        marketingConsentsOrder.indexOf(a.Channel) -
        marketingConsentsOrder.indexOf(b.Channel)
    );
  };

  useEffect(() => {
    const partnerId =
      selectedPartner || currentPartner?.ID || partnersList?.[0]?.ID;
    setSelectedPartner(partnerId);
    setSelectedPartnerAndBrand(
      partnersList?.find((item) => item.ID === partnerId)
    );
    setSelectedBrandConsent(consentGroupedByChannel(partnerId as number));
  }, [currentPartner?.ID, consentPartners.data, consentItems.data]);

  const applicationBrandConsent =
    normalizeConsentItems(currentPartner?.ID as number)?.filter((consent) =>
      !showInactiveTags ? consent.State === ConsentStateType.Given : []
    ) ?? [];
  const applicationGeneralBrandConsent =
    normalizeConsentItems(generalConsentPartner?.ID as number)?.filter(
      (consent) =>
        !showInactiveTags ? consent.State === ConsentStateType.Given : []
    ) ?? [];

  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    application.data?.MarketCountry
  );

  const hasWritePermission = useHasAnyOfRoles([
    permissions.RoleName.Manager,
    permissions.RoleName.Sales,
    permissions.RoleName.SecuredSales,
    permissions.RoleName.Telemarketing,
  ]);

  const isManager = useHasAnyOfRoles([permissions.RoleName.Manager]);

  const customerData = customer.data;

  const revokeContext = {
    market: application.data?.MarketCountry,
    brand: application.data?.Meta?.Brand,
    customerID,
    rawConsentItems: consentItems.data?.Items,
  };

  const handleRevokeForBrand = async () => {
    await patchPartner.mutateAsync({
      country: application.data?.MarketCountry as MarketCountryCode,
      customerID,
      patchData: buildRevokeForPartnerPatch(selectedPartner, revokeContext),
    });
  };

  const handleRevokeForAll = async () => {
    await patchPartner.mutateAsync({
      country: application.data?.MarketCountry as MarketCountryCode,
      customerID,
      patchData: buildRevokeForAllPatch(partnerIds, revokeContext),
    });
  };

  const _onAddConsentClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      // Propagation is stopped to prevent collapsing the AceCardSection
      e.stopPropagation();

      addMarketingConsent({
        configId: consentConfig?.ID,
        agentID: user.clientID,
        applicationId: application.data?.ID as string,
        country: application.data?.MarketCountry as MarketCountryCode,
        customerID: customerID,
        originBrandID: application.data?.Meta?.Brand as string,
      });
    },
    [
      addMarketingConsent,
      application.data?.MarketCountry,
      customerID,
      application.data?.Meta?.Brand,
      user.clientID,
      application.data?.ID,
      consentConfig,
    ]
  );

  const market = application.data?.MarketCountry;
  const isMarketingGiven = isMarketingConsentGiven(
    selectedBrandConsent,
    market
  );

  const channelColumns: {
    channel: CommunicationChannelType;
    label: string;
    isDisabled: (consent: ConsentData) => boolean;
  }[] = [
    {
      channel: CommunicationChannelType.Email,
      label: 'Email',
      isDisabled: (c) => c.State !== ConsentStateType.Given,
    },
    {
      channel: CommunicationChannelType.Sms,
      label: 'SMS',
      isDisabled: (c) => c.State !== ConsentStateType.Given,
    },
    {
      channel: CommunicationChannelType.Phone,
      label: 'Call',
      // Outside Finland a Call consent can also be switched back on.
      isDisabled: (c) =>
        market === MarketCountryCode.Finland &&
        c.State !== ConsentStateType.Given,
    },
    {
      channel: CommunicationChannelType.AiPhone,
      label: 'AI Call',
      isDisabled: (c) => c.State !== ConsentStateType.Given,
    },
  ];

  const renderTags = (items: ConsentData[], keyPrefix: string) =>
    items.map((item) => (
      <MarketingConsentTag
        key={[
          keyPrefix,
          item.PartnerID,
          item.CustomerID,
          item.OriginBrandID,
          item.Channel,
          applicationID,
        ].join('/')}
        isAllowed={item.State === ConsentStateType.Given}
        text="MKT"
        icon={getIconByChannelType(item.Channel)}
      />
    ));

  return customerData ? (
    <AceCardSection
      onCollapsed={onCollapsedAceCardSection}
      id={`MarketingConsent.${applicationID}`}
      title={
        <div className={styles.title}>
          Marketing consents:
          <div className={styles.tagsContainer}>
            <BrandLogo
              brand={generalConsentBrand as Brand}
              fallback=""
              className={styles.brand}
            />
            <div className={styles.tags}>
              {renderTags(applicationGeneralBrandConsent, 'general')}
              <Button
                onClick={_onAddConsentClick}
                icon="add"
                disabled={isAddConsentButtonDisabled}
                loading={isAddingConsent}
              >
                SMS/Email
              </Button>
            </div>
          </div>
          {currentPartner?.ID !== generalConsentPartner?.ID && (
            <div className={styles.tagsContainer}>
              <BrandLogo
                brand={application.data?.Meta?.Brand as Brand}
                fallback=""
                className={styles.brand}
              />
              <div className={styles.tags}>
                {renderTags(applicationBrandConsent, 'marketing')}
              </div>
            </div>
          )}
        </div>
      }
    >
      <Divider />
      {partnersList?.length ? (
        <div className={styles.partners}>
          <label htmlFor={partnerSelectId} className={styles.label}>
            Partner entity:
          </label>
          <select
            id={partnerSelectId}
            value={selectedPartner}
            onChange={setSelectedPartnerOption}
          >
            {partnersList?.map((partner, index) => (
              <option key={partner.ID ?? index} value={partner.ID}>
                {partner.Name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <div className={styles.consents}>
        <Switch
          className={styles.masterSwitch}
          label="Marketing consent"
          alignIndicator="right"
          checked={isMarketingGiven}
          // Only switches off; marketing consent is re-added with "SMS/Email".
          disabled={
            !isMarketingGiven ||
            !isManager ||
            !hasLock.data ||
            customer.isFetching ||
            patchPartner.isLoading
          }
          onChange={() => handleRevokeForBrand()}
        />
        <div className={styles.channels}>
          {channelColumns.map(({ channel, label, isDisabled }) => {
            const consent = selectedBrandConsent?.[channel]?.[0];
            return (
              <div key={channel} className={styles.channel}>
                <span className={Classes.TEXT_MUTED}>{label}</span>
                {consent && (
                  <ConsentSwitch
                    consentData={consent}
                    label={label}
                    onChecked={onCheckedConsentSwitch}
                    disabled={
                      (!consent.State && !consent.PartnerID) ||
                      isDisabled(consent) ||
                      !hasLock.data ||
                      customer.isFetching ||
                      !hasWritePermission
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.consentRow}>
        <span
          className={styles.consentLabel}
        >{`Marketing consent (EMAIL or SMS${
          market === MarketCountryCode.Finland ? ' or Call' : ''
        })`}</span>
        <span className={classNames(styles.consentValue, Classes.TEXT_MUTED)}>
          {isMarketingGiven ? 'YES' : 'NO'}
        </span>
      </div>
      <div className={styles.consentRow}>
        {selectedPartnerAndBrand && (
          <Button
            outlined
            small
            intent="danger"
            disabled={
              isMarketingConsentRevoked(selectedBrandConsent, market) ||
              !isManager ||
              !hasLock.data
            }
            className={styles.consentControl}
            onClick={() => handleRevokeForBrand()}
          >
            {`Revoke for ${selectedPartnerAndBrand?.Name}`}
          </Button>
        )}
        <Button
          outlined
          small
          intent="danger"
          disabled={
            !hasAnyGivenMarketing(consentItems.data?.Items, partnerIds) ||
            !isManager ||
            !hasLock.data
          }
          className={styles.consentControl}
          onClick={() => handleRevokeForAll()}
        >
          Revoke for all
        </Button>
      </div>
      <TransactionalMuteSection
        applicationID={applicationID}
        customerID={customerID}
      />
    </AceCardSection>
  ) : null;
}
